import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, ShieldAlert, Mail, ArrowLeft, RotateCcw } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { storeAuth, getStoredToken, getStoredUser, clearAuth } from '@/lib/authStore';
import { authApi } from '@/lib/api';
import { Toaster, toast } from 'sonner';
import { ForgotPasswordModal } from './AuthPage';

const OTP_LENGTH = 6;

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('password'); // 'password' | 'otp'
  const [otpSession, setOtpSession] = useState(null); // { otpSessionId, maskedEmail, expiresInMinutes }
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [otpError, setOtpError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const otpInputRefs = useRef([]);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm();

  // If an already-verified admin session exists, skip straight to the dashboard.
  useEffect(() => {
    const token = getStoredToken();
    const storedUser = getStoredUser();
    if (token && storedUser?.role === 'admin') {
      navigate('/admin', { replace: true });
    }
  }, [navigate]);

  const requestOtp = async (data) => {
    try {
      const res = await authApi.adminLoginStart({ email: data.email, password: data.password });
      setOtpSession(res);
      setOtpDigits(Array(OTP_LENGTH).fill(''));
      setOtpError('');
      setStep('otp');
      toast.success(`Verification code sent to ${res.maskedEmail} via email.`);
      setTimeout(() => otpInputRefs.current[0]?.focus(), 50);
    } catch (err) {
      toast.error(err.message || 'Could not start admin login.');
    }
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...otpDigits];
    next[index] = digit;
    setOtpDigits(next);
    setOtpError('');
    if (digit && index < OTP_LENGTH - 1) otpInputRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setOtpDigits(next);
    otpInputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleVerify = async () => {
    const code = otpDigits.join('');
    if (code.length !== OTP_LENGTH) {
      setOtpError(`Enter the ${OTP_LENGTH}-digit code.`);
      return;
    }
    setVerifying(true);
    setOtpError('');
    try {
      const { token, user } = await authApi.adminVerifyOtp({ otpSessionId: otpSession.otpSessionId, code });
      storeAuth(token, user);
      toast.success('Admin identity verified.');
      setTimeout(() => navigate('/admin', { replace: true }), 300);
    } catch (err) {
      setOtpError(err.message || 'Incorrect code.');
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const { email, password } = getValues();
      const res = await authApi.adminLoginStart({ email, password });
      setOtpSession(res);
      setOtpDigits(Array(OTP_LENGTH).fill(''));
      setOtpError('');
      toast.success(`New code sent to ${res.maskedEmail}.`);
    } catch (err) {
      toast.error(err.message || 'Could not resend the code.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ backgroundColor: 'var(--foreground)' }}>
      <div className="absolute inset-0 ledger-grid opacity-10 pointer-events-none" />

      <div className="relative z-10 w-full max-w-sm">
        <div className="flex flex-col items-center mb-6">
          <AppLogo size={44} />
          <h1
            className="font-heading text-lg tracking-stamp mt-3 flex items-center gap-2"
            style={{ color: 'var(--background)', fontFamily: 'var(--font-heading)' }}
          >
            <ShieldAlert size={18} style={{ color: 'var(--primary)' }} />
            ADMIN PORTAL
          </h1>
          <p className="text-xs mt-1 text-center" style={{ color: 'rgba(237,232,220,0.6)' }}>
            Restricted access · password + email OTP required
          </p>
        </div>

        <div className="paper-texture border-2 border-foreground ink-shadow p-6">
          {step === 'password' ? (
            <form onSubmit={handleSubmit(requestOtp)} className="flex flex-col gap-4">
              <div>
                <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                  ADMIN EMAIL <span style={{ color: 'var(--primary)' }}>*</span>
                </label>
                <input
                  type="email"
                  className="input-ledger w-full px-3 py-2.5 text-sm"
                  placeholder="example@gamil.com"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                  })}
                />
                {errors.email && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.email.message}</p>}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
                    PASSWORD <span style={{ color: 'var(--primary)' }}>*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-xs underline"
                    style={{ color: 'var(--primary)' }}
                  >
                    Forgot password?
                  </button>
                </div>
                <PasswordField register={register} errors={errors} />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary px-6 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {isSubmitting ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    SENDING CODE...
                  </>
                ) : (
                  'CONTINUE'
                )}
              </button>

              <p className="text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
                A one-time code will be sent to the admin's registered email address.
              </p>
            </form>
          ) : (
            <div className="flex flex-col gap-4">
              <button
                onClick={() => setStep('password')}
                className="flex items-center gap-1 text-xs font-heading tracking-stamp self-start hover:opacity-70 transition-opacity"
                style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}
              >
                <ArrowLeft size={12} /> BACK
              </button>

              <div className="flex items-start gap-2 p-3 border" style={{ borderColor: 'var(--secondary)', backgroundColor: 'var(--success-bg)' }}>
                <Mail size={16} style={{ color: 'var(--secondary)', flexShrink: 0, marginTop: '1px' }} />
                <p className="text-xs" style={{ color: 'var(--foreground)' }}>
                  A {OTP_LENGTH}-digit code was sent via email to{' '}
                  <span className="font-mono-data" style={{ fontFamily: 'var(--font-mono)' }}>{otpSession?.maskedEmail}</span>.
                  It expires in {otpSession?.expiresInMinutes ?? 5} minutes.
                </p>
              </div>

              <div>
                <label className="block text-xs font-heading tracking-stamp mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  ENTER VERIFICATION CODE
                </label>
                <div className="flex gap-2 justify-between" onPaste={handleOtpPaste}>
                  {otpDigits.map((digit, i) => (
                    <input
                      key={`otp-${i}`}
                      ref={(el) => (otpInputRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      className="input-ledger text-center text-lg font-mono-data"
                      style={{ width: '42px', height: '48px', fontFamily: 'var(--font-mono)' }}
                    />
                  ))}
                </div>
                {otpError && <p className="text-xs mt-2" style={{ color: 'var(--danger)' }}>{otpError}</p>}
              </div>

              <button
                onClick={handleVerify}
                disabled={verifying}
                className="btn-primary px-6 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {verifying ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    VERIFYING...
                  </>
                ) : (
                  'VERIFY & SIGN IN'
                )}
              </button>

              <button
                onClick={handleResend}
                disabled={resending}
                className="btn-secondary px-4 py-2 text-xs flex items-center justify-center gap-1.5 disabled:opacity-60"
              >
                <RotateCcw size={12} className={resending ? 'animate-spin' : ''} />
                {resending ? 'RESENDING...' : 'RESEND CODE'}
              </button>
            </div>
          )}
        </div>

        <p className="text-xs text-center mt-4" style={{ color: 'rgba(237,232,220,0.4)' }}>
          Not an admin? <a href="/login" className="underline" style={{ color: 'var(--primary)' }}>Go to the seller/buyer login</a>
        </p>
      </div>

      {showForgotPassword && (
        <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
      )}

      <Toaster position="bottom-right" richColors />
    </div>
  );
}

function PasswordField({ register, errors }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        className="input-ledger w-full px-3 py-2.5 text-sm pr-10"
        placeholder="••••••••"
        {...register('password', { required: 'Password is required' })}
      />
      <button
        type="button"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => setShow(!show)}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
      {errors.password && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.password.message}</p>}
    </div>
  );
}
